// UI tour — the shell-level coordinator for the welcome panel + page callouts.
//
// NAMING: this is `tour`, never "onboarding". The product already owns an
// onboarding module (entity_onboarding_*, the HR new-hire flow) and two
// different "onboarding" concepts on one screen would mislead the user.
//
// AUTHORITY: the step list is derived — `buildTourSteps(visibleKeys)` over the
// SAME filtered menu the left rail renders (components/use-visible-menu). No
// second catalog, no second permission predicate: a caller is introduced to
// exactly the pages they can reach, and a page added to the menu joins the
// tour for free.
//
// The panel auto-opens ONCE per version, and only after both authority signals
// land (`resolved`): opening mid-flight would introduce the user to a default-
// closed SUBSET of their product and then never re-open, because acknowledging
// writes seen_version. Where that acknowledgement is REMEMBERED is a deployment
// choice — see isTourSessionScoped below.
//
// POSITION lives here, not in the panel, and persists for the session. The
// panel used to own it as local state reset to 0 on every open — which made the
// walk unfinishable in two ordinary ways: a reload restarted it, and following
// the panel's own "open this page" link ended it outright with nothing
// remembering where the reader was.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useVisibleMenu } from "@/components/use-visible-menu";
import { cn } from "@/lib/utils";

import { fetchUiTourState, saveUiTourSeenVersion } from "./api";
import { buildTourSteps, TOUR_VERSION, type TourStep } from "./catalog";

const CALLOUT_DISMISS_STORAGE_KEY = "novemai.tour.callouts-dismissed";
const STEP_INDEX_STORAGE_KEY = "novemai.tour.step-index";
const SESSION_SEEN_STORAGE_KEY = "novemai.tour.seen-version";

/**
 * Where the acknowledgement is REMEMBERED — the durable per-employee column, or
 * this browser session.
 *
 * The durable marker is right for a real tenant: a person is introduced to the
 * product once, on the machine of their choosing, and never again. It is wrong
 * for a demo, where ONE seeded persona login is handed to many different
 * people: the first visitor who clicks "Bitir" writes seen_version, and every
 * visitor after them is silently denied the introduction the deployment exists
 * to give. The two audiences want opposite things from the same code, which is
 * what makes this a deployment flag rather than a product decision.
 *
 * Session scope — not "never remember" — because acknowledging still has to
 * mean something for the length of a visit: the panel must stay shut across a
 * reload and across the hand-off to a real page, exactly like the step index
 * beneath it. A fresh browser session is what starts the tour over.
 *
 * `"on"` is the only truthy spelling in this repo; anything else (including
 * `"true"`) leaves the durable behaviour in place.
 */
function isTourSessionScoped(): boolean {
  return import.meta.env["VITE_TOUR_SESSION_SCOPED"] === "on";
}

function readSessionSeenVersion(): number | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_SEEN_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isInteger(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function persistSessionSeenVersion(version: number): void {
  try {
    window.sessionStorage.setItem(SESSION_SEEN_STORAGE_KEY, String(version));
  } catch {
    /* best effort — the in-memory state still holds for this page load */
  }
}

interface TourContextValue {
  readonly steps: readonly TourStep[];
  readonly panelOpen: boolean;
  /**
   * Where the walk stands. Lives HERE and persists, because the two surfaces
   * that move it (the panel's Next/Back, and "Sayfayı aç" handing the user off
   * to a real page) are different components and a reload sits between them.
   * Clamped by the reader — a caller whose authority shrinks mid-session can
   * have a stored index past the end of their step list.
   */
  readonly stepIndex: number;
  readonly setStepIndex: (next: number) => void;
  /**
   * The user is mid-tour: steps exist and they have not acknowledged it. Drives
   * the resume affordance on the page they were sent to.
   */
  readonly inProgress: boolean;
  readonly openPanel: () => void;
  /**
   * Close WITHOUT persisting — "not now". Reachable by Escape, and implied by
   * any route change (see the pathname effect).
   *
   * The five-step tour dropped the "Don't show again" BUTTON (at 37 steps
   * "later" and "never" were genuinely different answers; at five they are
   * not, so Skip now acknowledges). Escape is the case that survives the
   * simplification, and it survives for a reason a test already pinned: a
   * build that wrote seen_version on EVERY dismissal would silence the tour
   * forever on one stray key press. A deliberate click and an accidental key
   * are not the same intent.
   */
  readonly closePanel: () => void;
  /** Close AND persist the acknowledgement (PUT /v1/me/ui-tour). */
  readonly acknowledge: () => void;
  readonly stepForPath: (pathname: string) => TourStep | undefined;
  readonly isCalloutDismissed: (stepKey: string) => boolean;
  readonly dismissCallout: (stepKey: string) => void;

  /**
   * The screen tour the reader asked for, or null.
   *
   * NOT persisted, unlike the shell tour's position. The shell tour had to
   * survive a reload and a trip to a real page because the two surfaces that
   * moved it were different components with navigation between them. A screen
   * tour is asked for, runs inside one screen, and ends when the route changes
   * — persisting it would drop someone into the middle of a walk they
   * abandoned yesterday.
   */
  readonly activeScreenTour: { readonly menuKey: string; readonly stepIndex: number } | null;
  readonly startScreenTour: (menuKey: string) => void;
  readonly endScreenTour: () => void;
  readonly setScreenTourStep: (next: number) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

/**
 * Callout dismissals are SESSION scope on purpose: a per-page hint the user
 * waved away should stay gone while they work, and come back next sign-in —
 * localStorage would silence it forever on the strength of one stray click,
 * and the panel (which does persist) is the deliberate "stop showing me this".
 */
function readDismissedCallouts(): ReadonlySet<string> {
  try {
    const raw = window.sessionStorage.getItem(CALLOUT_DISMISS_STORAGE_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((v): v is string => typeof v === "string")) : new Set();
  } catch {
    // Private-mode / quota-blocked sessionStorage must not break the shell.
    return new Set();
  }
}

function persistDismissedCallouts(keys: ReadonlySet<string>): void {
  try {
    window.sessionStorage.setItem(CALLOUT_DISMISS_STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    // Best-effort only — the in-memory Set still holds for this page load.
  }
}

/**
 * Session scope, like the callout dismissals: the walk should survive a reload
 * and a trip to a real page, and start clean at the next sign-in. localStorage
 * would strand someone forever at step 14 of a tour they no longer remember.
 */
function readStepIndex(): number {
  try {
    const raw = window.sessionStorage.getItem(STEP_INDEX_STORAGE_KEY);
    if (raw === null) return 0;
    const parsed = Number.parseInt(raw, 10);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function persistStepIndex(index: number): void {
  try {
    window.sessionStorage.setItem(STEP_INDEX_STORAGE_KEY, String(index));
  } catch {
    /* best effort */
  }
}

export function TourProvider({ children }: { readonly children: ReactNode }): React.ReactElement {
  const { visibleKeys, resolved } = useVisibleMenu();
  const queryClient = useQueryClient();

  const steps = useMemo(() => buildTourSteps(visibleKeys), [visibleKeys]);

  const sessionScoped = isTourSessionScoped();

  const [panelOpen, setPanelOpen] = useState(false);
  const [storedStepIndex, setStoredStepIndex] = useState<number>(readStepIndex);
  const [sessionSeenVersion, setSessionSeenVersion] = useState<number | null>(readSessionSeenVersion);
  const [dismissedCallouts, setDismissedCallouts] = useState<ReadonlySet<string>>(readDismissedCallouts);
  const [activeScreenTour, setActiveScreenTour] = useState<{
    menuKey: string;
    stepIndex: number;
  } | null>(null);

  // Rota değişince ekran turu biter: adımlar BİR ekrana aittir. Temizlemezsek
  // /people'ın 3. adımı /payroll'da çözülemeyen bir çapaya bakar ve tur sessizce
  // kaybolur — kullanıcı için "tıkladım, hiçbir şey olmadı".
  //
  // Kabuk turu da aynı olayda KAPANIR, ama onaylanmadan: Escape ile aynı "şimdi
  // değil" cevabı. Panel modal olmadığı için, kapatmayan bir build onu kullanıcı
  // Atla'ya basana kadar her sayfada içeriğin üstünde taşıyordu (E20 — "tur her
  // sayfayı takip ediyor"). Kaldığı adım saklı kalıyor; "?" oradan devam ettirir.
  // İlk çalıştırma atlanır: kapatılacak bir şey yokken efekt açılışı geri almasın.
  const { pathname } = useLocation();
  const lastPathnameRef = useRef(pathname);
  useEffect(() => {
    setActiveScreenTour(null);
    if (lastPathnameRef.current === pathname) return;
    lastPathnameRef.current = pathname;
    setPanelOpen(false);
  }, [pathname]);

  const startScreenTour = useCallback((menuKey: string): void => {
    setActiveScreenTour({ menuKey, stepIndex: 0 });
  }, []);
  const endScreenTour = useCallback((): void => {
    setActiveScreenTour(null);
  }, []);
  const setScreenTourStep = useCallback((next: number): void => {
    setActiveScreenTour((prev) => (prev === null ? null : { ...prev, stepIndex: Math.max(0, next) }));
  }, []);

  // Fail-closed on error: no answer means we do not know whether the user has
  // already seen the tour, and a panel that reappears on every load is worse
  // than one that never does. `retry: false` keeps a missing endpoint from
  // firing a retry storm on every navigation.
  const tourState = useQuery({
    queryKey: ["me", "ui-tour"],
    queryFn: fetchUiTourState,
    staleTime: Infinity,
    retry: false,
  });

  // İyimser yazım: önbellek PUT dönmeden ÖNCE "gördü" olur, dönünce değil.
  // Kabuk yeniden mount olursa auto-open mandalı sıfırlanır ve önbellekte hâlâ
  // null duruyorsa panel Atla'dan sonra yeniden açılır; yavaş bir PUT tam olarak
  // o pencereyi açar. Hata olursa GERİ ALINMIYOR: kullanıcı yazımın başarısını
  // göremez, ve az önce "Atla" dediği turu ona geri vermek sessiz bir
  // başarısızlıktan daha kötü. Sonraki sayfa yüklemesi sunucuyu yeniden okur ve
  // gerçek durumu o söyler.
  const acknowledgeMutation = useMutation({
    mutationFn: () => saveUiTourSeenVersion(TOUR_VERSION),
    onMutate: () => {
      queryClient.setQueryData<{ seen_version: number | null }>(["me", "ui-tour"], {
        seen_version: TOUR_VERSION,
      });
    },
  });

  // One auto-open per page load. Without this latch, closing the panel before
  // the acknowledgement round-trip lands would immediately re-open it.
  const autoOpenedRef = useRef(false);
  const seenVersion = sessionScoped ? sessionSeenVersion : tourState.data?.seen_version;
  // The GET is a precondition only when the SERVER holds the answer. Gating the
  // session-scoped path on a call it no longer reads would let one 503 hide the
  // tour a demo deployment exists to show.
  const markerReady = sessionScoped || tourState.isSuccess;
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!markerReady || !resolved || steps.length === 0) return;
    if (seenVersion === undefined) return;
    if (seenVersion !== null && seenVersion >= TOUR_VERSION) return;
    autoOpenedRef.current = true;
    setPanelOpen(true);
  }, [markerReady, resolved, steps.length, seenVersion]);

  const openPanel = useCallback(() => {
    autoOpenedRef.current = true;
    setPanelOpen(true);
  }, []);
  const closePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);
  const acknowledge = useCallback(() => {
    setPanelOpen(false);
    // Acknowledging ends the walk — a later "?" reopen starts a fresh one
    // rather than dropping the reader back into the middle of a tour they
    // deliberately finished.
    setStoredStepIndex(0);
    persistStepIndex(0);
    if (sessionScoped) {
      // Deliberately NO write to /v1/me/ui-tour: the durable column is shared
      // by everyone who signs in as this persona, so persisting here is exactly
      // the lockout this mode exists to avoid.
      setSessionSeenVersion(TOUR_VERSION);
      persistSessionSeenVersion(TOUR_VERSION);
      return;
    }
    acknowledgeMutation.mutate();
  }, [acknowledgeMutation, sessionScoped]);

  const stepIndex = steps.length === 0 ? 0 : Math.min(storedStepIndex, steps.length - 1);
  const setStepIndex = useCallback((next: number) => {
    const clamped = Math.max(0, next);
    setStoredStepIndex(clamped);
    persistStepIndex(clamped);
  }, []);

  const inProgress =
    steps.length > 0 &&
    markerReady &&
    (seenVersion === null || seenVersion === undefined || seenVersion < TOUR_VERSION);

  const stepForPath = useCallback(
    (pathname: string): TourStep | undefined => {
      // Exact first, then the longest href the path sits under, so a detail
      // route (/people/:id) still shows its section's callout while
      // /me/leave/balance never resolves to /me/leave.
      const exact = steps.find((step) => step.href === pathname);
      if (exact !== undefined) return exact;
      let best: TourStep | undefined;
      for (const step of steps) {
        if (step.href === "/") continue;
        if (!pathname.startsWith(`${step.href}/`)) continue;
        if (best === undefined || step.href.length > best.href.length) best = step;
      }
      return best;
    },
    [steps],
  );

  const isCalloutDismissed = useCallback(
    (stepKey: string): boolean => dismissedCallouts.has(stepKey),
    [dismissedCallouts],
  );
  const dismissCallout = useCallback((stepKey: string) => {
    setDismissedCallouts((prev) => {
      const next = new Set(prev);
      next.add(stepKey);
      persistDismissedCallouts(next);
      return next;
    });
  }, []);

  const value = useMemo<TourContextValue>(
    () => ({
      steps,
      panelOpen,
      stepIndex,
      setStepIndex,
      inProgress,
      openPanel,
      closePanel,
      acknowledge,
      stepForPath,
      isCalloutDismissed,
      dismissCallout,
      activeScreenTour,
      startScreenTour,
      endScreenTour,
      setScreenTourStep,
    }),
    [
      steps,
      panelOpen,
      stepIndex,
      setStepIndex,
      inProgress,
      openPanel,
      closePanel,
      acknowledge,
      stepForPath,
      isCalloutDismissed,
      dismissCallout,
      activeScreenTour,
      startScreenTour,
      endScreenTour,
      setScreenTourStep,
    ],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

/**
 * Tour context, or `null` outside a provider. Nullable rather than throwing:
 * the help button and the page callout mount inside the shell but are rendered
 * by tests and stories that have no reason to stand up the whole tour.
 */
export function useTour(): TourContextValue | null {
  return useContext(TourContext);
}

/** Top-bar "?" — reopens the welcome panel at any time. */
export function TourHelpButton(): React.ReactElement | null {
  const { t } = useTranslation();
  const tour = useTour();
  if (tour === null || tour.steps.length === 0) return null;
  return (
    <button
      type="button"
      onClick={tour.openPanel}
      aria-label={t("tour.action.reopen")}
      title={t("tour.action.reopen")}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-[8px] border border-[var(--border-default)]",
        "bg-[var(--bg-raised)] text-[var(--fg-secondary)] hover:bg-[var(--bg-subtle)]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
      )}
    >
      <HelpCircle size={16} aria-hidden="true" />
    </button>
  );
}
